import { EmptyView, emptyViewVariants } from '@components'

import { Flex, Icon, Spacing, Text, useDrawer } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { queryClient } from '../../../../../../config/queryClient'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { TrackingContext } from '../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../hooks'
import { useFetchScoutConnections } from '../../../../hooks/useFetchScoutConnections'
import { useScoutAccess } from '../../../../hooks/useScoutAccess'
import { scoutEmptyStateViewVariants } from './constants/scoutEmptyStateVariants'
import { ScoutConnectionStatus } from './enums/scoutConnectionStatus'
import { ScoutEmptyStateMode } from './enums/scoutEmptyStateMode'
import * as Styled from './styles'

interface ScoutEmptyStateProps {
  mode: ScoutEmptyStateMode
}

export const ScoutEmptyState = ({ mode }: ScoutEmptyStateProps): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { isOpen: isScoutDrawerOpen, open: openScoutDrawer } = useDrawer()
  const { isLockedByUpsell } = useScoutAccess()
  const { connections } = useFetchScoutConnections({
    onScoutProcessingEnd: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.AttachmentsList })
      queryClient.invalidateQueries({ queryKey: QueryKeys.AttachmentsCount })
    },
    shouldRefetch: mode === ScoutEmptyStateMode.FilePicker,
  })
  const isConnected = connections && connections.length > 0
  const isProcessing = connections?.some((connection) => connection.isProcessing)
  let scoutStatus: ScoutConnectionStatus = isConnected
    ? ScoutConnectionStatus.Connected
    : ScoutConnectionStatus.NotConnected
  scoutStatus = isProcessing ? ScoutConnectionStatus.Processing : scoutStatus
  const emptyViewVariantName = scoutEmptyStateViewVariants[mode][scoutStatus]
  const { animation, animationDimensions, illustration, buttonText, description, title } =
    emptyViewVariants[emptyViewVariantName]
  const showAutomationButton =
    !isScoutDrawerOpen && mode === ScoutEmptyStateMode.UploadPage && scoutStatus === ScoutConnectionStatus.NotConnected
  const showConnectionInstructions =
    mode === ScoutEmptyStateMode.FilePicker && scoutStatus === ScoutConnectionStatus.NotConnected

  const handleOpenAutomationSidePanelButtonClick = useCallback(() => {
    openScoutDrawer()
    track('Automation Side Panel Opened (Client)', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      trigger_source: 'EmptyStateButton',
    })
  }, [openScoutDrawer, track])

  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={Spacing.S24}>
      <EmptyView
        animationName={animation}
        animationDimensions={animationDimensions}
        illustration={illustration}
        isLockedByUpsell={isLockedByUpsell}
        onClick={handleOpenAutomationSidePanelButtonClick}
        showCTA={showAutomationButton}
        tButton={buttonText}
        tDescription={description}
        tTitle={title}
        trackingContext={TrackingContext.Scout}
      />
      {showConnectionInstructions && (
        <>
          <Flex gap={Spacing.S8} alignItems="center">
            <Styled.IconWrapper>
              <Icon icon="zap" />
            </Styled.IconWrapper>
            <Text variant="h4">{t('empty_state.file_picker.scout.not_connected.subtitle')}</Text>
          </Flex>
          <Text colorVariant="secondary">
            <Trans
              i18nKey="empty_state.file_picker.scout.connect.steps"
              components={{
                b: <b />,
              }}
            />
          </Text>
        </>
      )}
    </Flex>
  )
}
